// Notification Component
// Displays temporary notifications for connection status and other messages

import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

const Notification = ({ 
  message, 
  type = 'info', // 'success', 'error', 'warning', 'info'
  duration = 5000,
  onClose 
}) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => {
          onClose?.();
        }, 300); // Wait for fade out animation
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose?.();
    }, 300);
  };

  const getConfig = () => {
    switch (type) {
      case 'success':
        return {
          icon: <CheckCircle className="w-5 h-5" />,
          bgClass: 'bg-green-50 border-green-200',
          textClass: 'text-green-800',
          iconClass: 'text-green-600'
        };
      case 'error':
        return {
          icon: <AlertCircle className="w-5 h-5" />,
          bgClass: 'bg-red-50 border-red-200',
          textClass: 'text-red-800',
          iconClass: 'text-red-600'
        };
      case 'warning':
        return {
          icon: <AlertTriangle className="w-5 h-5" />,
          bgClass: 'bg-yellow-50 border-yellow-200',
          textClass: 'text-yellow-800',
          iconClass: 'text-yellow-600'
        };
      default:
        return {
          icon: <Info className="w-5 h-5" />,
          bgClass: 'bg-blue-50 border-blue-200',
          textClass: 'text-blue-800',
          iconClass: 'text-blue-600'
        };
    }
  };

  const config = getConfig();

  return (
    <div
      className={`notification flex items-start gap-3 p-4 rounded-lg border shadow-lg transition-all duration-300 ${
        isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'
      } ${config.bgClass} ${config.textClass}`}
      role="alert"
    >
      <div className={config.iconClass}>
        {config.icon}
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium">{message}</p>
      </div>
      <button
        onClick={handleClose}
        className="text-gray-400 hover:text-gray-600 transition-colors"
        aria-label="關閉通知"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// Notification Manager Component
export const NotificationManager = () => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = (message, type = 'info', duration = 5000) => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { id, message, type, duration }]);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };

  useEffect(() => {
    // Listen for custom notification events
    const handleNotification = (event) => {
      const { message, type, duration } = event.detail;
      addNotification(message, type, duration);
    };

    window.addEventListener('showNotification', handleNotification);

    // Expose addNotification globally for easy access
    window.showNotification = addNotification;

    return () => {
      window.removeEventListener('showNotification', handleNotification);
      delete window.showNotification;
    };
  }, []);

  return (
    <div className="fixed top-4 right-4 z-[1000] space-y-2 max-w-md">
      {notifications.map((notif) => (
        <Notification
          key={notif.id}
          message={notif.message}
          type={notif.type}
          duration={notif.duration}
          onClose={() => removeNotification(notif.id)}
        />
      ))}
    </div>
  );
};

// Helper function to show notifications
export const showNotification = (message, type = 'info', duration = 5000) => {
  const event = new CustomEvent('showNotification', {
    detail: { message, type, duration }
  });
  window.dispatchEvent(event);
};

export default Notification;

